'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { CreditCard, Lock, Loader2, CheckCircle } from 'lucide-react'
import { useTranslations } from 'next-intl'

interface MockPaymentSheetProps {
  amount: number // in cents
  onSuccess: () => void
  onCancel: () => void
  description?: string
  inline?: boolean // If true, renders as inline content instead of a card
}

// Get a future expiration date (current month / current year + 3)
function getFutureExpiry(): string {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const year = String((now.getFullYear() + 3) % 100).padStart(2, '0')
  return `${month}/${year}`
}

export function MockPaymentSheet({ amount, onSuccess, onCancel, description, inline }: MockPaymentSheetProps) {
  const t = useTranslations('demo')
  const [isProcessing, setIsProcessing] = useState(false)
  const [isComplete, setIsComplete] = useState(false)
  
  const handleSubmit = async () => {
    setIsProcessing(true)
    
    // Simulate payment processing delay
    await new Promise(resolve => setTimeout(resolve, 800))
    
    setIsProcessing(false)
    setIsComplete(true)
    
    // Brief success state before closing
    setTimeout(() => {
      onSuccess()
    }, 600)
  }
  
  const formattedAmount = (amount / 100).toFixed(2)
  
  // Inline mode - renders directly without card wrapper
  if (inline) {
    if (isComplete) {
      return (
        <div className="p-6 text-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-400" />
            </div>
            <p className="text-lg font-semibold text-green-400">{t('paymentSuccessful')}</p>
            <p className="text-sm text-zinc-400">{t('demoPaymentCompleted', { amount: formattedAmount })}</p>
          </div>
        </div>
      )
    }
    
    return (
      <div className="p-4 space-y-4">
        {/* Demo Banner */}
        <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/20 border border-amber-500/30">
          <Lock className="h-4 w-4 text-amber-400 shrink-0" />
          <p className="text-xs text-amber-300">
            <span className="font-semibold">{t('demoMode')}</span> {t('noRealPayment')}
          </p>
        </div>
        
        {/* Amount Display */}
        <div className="text-center py-2">
          <p className="text-sm text-zinc-400">{description || t('paymentAmount')}</p>
          <p className="text-3xl font-bold text-white">${formattedAmount}</p>
        </div>
        
        {/* Fake Card Details */}
        <div className="space-y-3">
          <div className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
            <div className="flex items-center gap-3">
              <CreditCard className="h-5 w-5 text-zinc-400" />
              <div className="flex-1">
                <p className="text-sm font-mono text-zinc-300">•••• •••• •••• 4242</p>
                <div className="flex items-center gap-4 mt-1">
                  <span className="text-xs text-zinc-500">Exp: {getFutureExpiry()}</span>
                  <span className="text-xs text-zinc-500">CVC: •••</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
            <p className="text-xs text-zinc-500 mb-1">{t('cardholderName')}</p>
            <p className="text-sm text-zinc-300">{t('demoUser')}</p>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            className="flex-1 border-zinc-700 hover:bg-zinc-800"
            onClick={onCancel}
            disabled={isProcessing}
          >
            {t('cancel')}
          </Button>
          <Button
            className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
            onClick={handleSubmit}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('processing')}
              </>
            ) : (
              <>
                <Lock className="h-4 w-4 mr-2" />
                {t('payAmount', { amount: formattedAmount })}
              </>
            )}
          </Button>
        </div>
        
        {/* Fine Print */}
        <p className="text-[10px] text-zinc-600 text-center">
          {t('demoFinePrint')}
        </p>
      </div>
    )
  }
  
  // Card wrapped mode (original)
  if (isComplete) {
    return (
      <Card className="border-2 border-dashed border-green-500/50 bg-green-500/5">
        <CardContent className="p-6 text-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-400" />
            </div>
            <p className="text-lg font-semibold text-green-400">{t('paymentSuccessful')}</p>
            <p className="text-sm text-zinc-400">{t('demoPaymentCompleted', { amount: formattedAmount })}</p>
          </div>
        </CardContent>
      </Card>
    )
  }
  
  return (
    <Card className="border-2 border-dashed border-amber-500/50 bg-amber-500/5">
      <CardContent className="p-4 space-y-4">
        {/* Demo Banner */}
        <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/20 border border-amber-500/30">
          <Lock className="h-4 w-4 text-amber-400 shrink-0" />
          <p className="text-xs text-amber-300">
            <span className="font-semibold">{t('demoMode')}</span> {t('noRealPayment')}
          </p>
        </div>
        
        {/* Amount Display */}
        <div className="text-center py-2">
          <p className="text-sm text-zinc-400">{description || t('paymentAmount')}</p>
          <p className="text-3xl font-bold text-white">${formattedAmount}</p>
        </div>
        
        {/* Fake Card Details */}
        <div className="space-y-3">
          <div className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
            <div className="flex items-center gap-3">
              <CreditCard className="h-5 w-5 text-zinc-400" />
              <div className="flex-1">
                <p className="text-sm font-mono text-zinc-300">•••• •••• •••• 4242</p>
                <div className="flex items-center gap-4 mt-1">
                  <span className="text-xs text-zinc-500">Exp: {getFutureExpiry()}</span>
                  <span className="text-xs text-zinc-500">CVC: •••</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
            <p className="text-xs text-zinc-500 mb-1">{t('cardholderName')}</p>
            <p className="text-sm text-zinc-300">{t('demoUser')}</p>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            className="flex-1 border-zinc-700 hover:bg-zinc-800"
            onClick={onCancel}
            disabled={isProcessing}
          >
            {t('cancel')}
          </Button>
          <Button
            className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
            onClick={handleSubmit}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('processing')}
              </>
            ) : (
              <>
                <Lock className="h-4 w-4 mr-2" />
                {t('completePayment')}
              </>
            )}
          </Button>
        </div>
        
        {/* Fine Print */}
        <p className="text-[10px] text-zinc-600 text-center">
          {t('demoFinePrint')}
        </p>
      </CardContent>
    </Card>
  )
}

// Compact inline version for tip amounts
interface MockTipButtonProps {
  amount: number // in cents
  onTip: (amount: number) => void
  disabled?: boolean
}

export function MockTipButton({ amount, onTip, disabled }: MockTipButtonProps) {
  const [isProcessing, setIsProcessing] = useState(false)
  
  const handleClick = async () => {
    setIsProcessing(true)
    await new Promise(resolve => setTimeout(resolve, 500))
    onTip(amount)
    setIsProcessing(false)
  }
  
  return (
    <Button
      variant="outline"
      size="sm"
      className="border-green-500/30 text-green-400 hover:bg-green-500/10 hover:border-green-500/50"
      onClick={handleClick}
      disabled={disabled || isProcessing}
    >
      {isProcessing ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        `$${(amount / 100).toFixed(0)}`
      )}
    </Button>
  )
}

